import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Brain, Briefcase, FileText, Link2, MessageCircle, MessageSquare, Scale, Search, Shield, Sparkles, FileSearch, Clock, CheckCircle2, Users, TrendingUp, Target, FlaskConical, Zap } from 'lucide-react';
import PublicHeader from '../components/layout/PublicHeader';
import PublicFooter from '../components/layout/PublicFooter';
import config from '../config';

const Home: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <PublicHeader />

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 px-2">
            Your Managers Aren't Managing.
            <span className="block text-primary-600 mt-1 sm:mt-2">They're Being Human Search Engines.</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 max-w-4xl mx-auto leading-relaxed px-4">
            Your team spends more time hunting for information than using it. LIA turns fragmented knowledge into
            conversational intelligence. Ask any question. Get instant answers from across every system.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mb-8 sm:mb-12 px-4">
            <Link
              to="/how-to-use"
              className="bg-primary-600 text-white px-6 sm:px-10 py-3 sm:py-4 rounded-lg hover:bg-primary-700 transition-colors font-semibold text-base sm:text-lg inline-flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
            >
              See How LIA Answers Your Questions
              <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
            </Link>
            <a
              href="#calculate-cost"
              className="bg-white text-primary-600 px-6 sm:px-10 py-3 sm:py-4 rounded-lg border-2 border-primary-600 hover:bg-primary-50 transition-colors font-semibold text-base sm:text-lg inline-flex items-center justify-center shadow-md"
            >
              Calculate Your Information Cost
            </a>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-600 px-4">
            <div className="flex items-center justify-center">
              <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mr-2 flex-shrink-0" />
              <span>No Credit Card Required</span>
            </div>
            <div className="flex items-center justify-center">
              <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mr-2 flex-shrink-0" />
              <span>Setup in Minutes</span>
            </div>
            <div className="flex items-center justify-center">
              <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mr-2 flex-shrink-0" />
              <span>Built for Every Industry</span>
            </div>
            <div className="flex items-center justify-center">
              <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mr-2 flex-shrink-0" />
              <span>Data Sovereign</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section: The Time Theft */}
      <section className="bg-white py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-10 sm:mb-12 text-center">
            How Much of Your Week Is Spent Waiting for Someone to Find Information That Already Exists?
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
              <h3 className="font-bold text-gray-900 text-lg mb-2">Six calendars aligned. Ninety minutes blocked. One question asked.</h3>
              <p className="text-gray-600">How many of your meetings are just expensive search queries?</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
              <h3 className="font-bold text-gray-900 text-lg mb-2">The decision takes 10 minutes. Getting the context takes 10 days.</h3>
              <p className="text-gray-600">You're not slow at deciding. You're slow at getting the information to decide.</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 md:col-span-2">
              <h3 className="font-bold text-gray-900 text-lg mb-2">Right now, someone in your organisation is waiting for an answer you already have.</h3>
              <p className="text-gray-600">The information is there. The systems are there. The people are there. The answer is nowhere.</p>
            </div>
          </div>
          <p className="text-center text-xl text-gray-700 italic">Most of your meetings are just people admitting they don't have the complete picture.</p>
        </div>
      </section>

      {/* Absurdity Section: Consumer vs Enterprise */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-10 sm:mb-12 text-center">
          Your Smartphone Is Smarter Than Your Entire Enterprise
        </h2>
        <div className="grid sm:grid-cols-2 gap-6 mb-10">
          <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
            <h3 className="font-bold text-gray-900 mb-2">Consumer vs Finance</h3>
            <p className="text-gray-600">You can order anything in the world to your door in 24 hours. But you can't get an answer from your own finance department in 48.</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
            <h3 className="font-bold text-gray-900 mb-2">Amazon vs Your Organisation</h3>
            <p className="text-gray-600">Amazon knows what you want before you do. Your organisation doesn't know what it already knows.</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
            <h3 className="font-bold text-gray-900 mb-2">Google vs Internal Search</h3>
            <p className="text-gray-600">It's easier to Google the internet than to search your own organisation.</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
            <h3 className="font-bold text-gray-900 mb-2">The Mirror Test</h3>
            <p className="text-gray-600">If your organisation was a person, how long would it take them to answer a simple question about themselves?</p>
          </div>
        </div>
        <p className="text-center text-gray-600 italic">You have the data. You pay for the systems. You employ the experts. And you still can't get a simple answer.</p>
      </section>

      {/* The Gap Section */}
      <section className="bg-white py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-8 sm:mb-10 text-center">
            Your Organisation Is Brilliant. But Its Intelligence Is Scattered.
          </h2>
          <div className="space-y-4 max-w-3xl mx-auto mb-8">
            <p className="text-lg font-semibold text-gray-900">1. &ldquo;Everyone has a piece of the puzzle. Nobody has the picture.&rdquo;</p>
            <p className="text-lg font-semibold text-gray-900">2. &ldquo;The answer exists in fragments. Finance has one bit. Operations has another. You get neither.&rdquo;</p>
            <p className="text-lg font-semibold text-gray-900">3. &ldquo;The information exists. It's just trapped in systems that don't talk.&rdquo;</p>
          </div>
          <p className="text-center text-xl font-bold text-primary-600">You don't lack information. You lack access to it.</p>
        </div>
      </section>

      {/* Simple vs Complex Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 text-center">
          Simple Question: &ldquo;Why Are We Over Budget?&rdquo;
        </h2>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-10 text-center">
          Complex Answer: &ldquo;Let Me Schedule a Meeting with Finance, Ops, and Procurement.&rdquo;
        </h2>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div>
            <p className="font-bold text-red-600 mb-2">TODAY:</p>
            <p className="text-gray-700">One question. Three departments. Five systems. Two weeks. Zero sense.</p>
          </div>
          <div>
            <p className="font-bold text-green-600 mb-2">WITH LIA:</p>
            <p className="text-gray-700">One question. One answer. 30 seconds.</p>
          </div>
        </div>
        <div className="mt-10 space-y-3 max-w-2xl mx-auto text-center text-gray-600">
          <p>&ldquo;You're asking human questions. Your systems demand technical queries.&rdquo;</p>
          <p>&ldquo;The question is simple. Getting the answer is archaeology.&rdquo;</p>
          <p>&ldquo;Your CFO shouldn't need a technical degree to ask about costs.&rdquo;</p>
        </div>
      </section>

      {/* Competitive Urgency */}
      <section className="bg-gradient-to-r from-gray-900 to-gray-800 py-12 sm:py-16 lg:py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-10 sm:mb-12 text-center">
            While You're Waiting for Information, Your Competition Is Already Acting on It.
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
              <Zap className="h-10 w-10 text-white mb-4" />
              <h3 className="text-xl font-semibold mb-2">Speed</h3>
              <p className="text-gray-200">Your competitor just answered that question in 30 seconds. You're still scheduling the meeting.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
              <Target className="h-10 w-10 text-white mb-4" />
              <h3 className="text-xl font-semibold mb-2">Reality</h3>
              <p className="text-gray-200">Speed isn't about working faster. It's about knowing faster.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
              <MessageSquare className="h-10 w-10 text-white mb-4" />
              <h3 className="text-xl font-semibold mb-2">Market Truth</h3>
              <p className="text-gray-200">Your market doesn't care that you needed three departments to answer. They've already moved to someone who didn't.</p>
            </div>
          </div>
          <p className="text-center text-xl italic mb-8">The company that can ask and answer wins. Everyone else waits.</p>
          <p id="calculate-cost" className="text-center text-gray-200">
            Your information fragmentation has a price tag. Want to see it?{' '}
            <Link to="/contact" className="text-primary-300 hover:text-white font-semibold underline">Contact us for an ROI conversation</Link>.
          </p>
        </div>
      </section>

      {/* Reality Check: Investment vs Outcome */}
      <section className="bg-white py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-8 sm:mb-10 text-center">
            You've Got the Best Systems Money Can Buy. And You Still Can't Answer Simple Questions.
          </h2>
          <div className="space-y-4 max-w-3xl mx-auto mb-10">
            <p className="text-lg font-semibold text-gray-900">&ldquo;Ten years of digital transformation. You still need meetings to get information.&rdquo;</p>
            <p className="text-lg font-semibold text-gray-900">&ldquo;You migrated everything to the cloud. The information is still in silos.&rdquo;</p>
            <p className="text-lg font-semibold text-gray-900">&ldquo;Enterprise software promised intelligence. You got more systems that don't talk.&rdquo;</p>
            <p className="text-lg font-semibold text-gray-900">&ldquo;You spent millions on infrastructure. But can you ask it a question?&rdquo;</p>
          </div>
          <p className="text-center text-xl font-bold text-primary-600">The systems work. The connections don't. That's what LIA solves.</p>
        </div>
      </section>

      {/* The Transformation: What LIA Actually Does */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-10 sm:mb-12 text-center">
          From Fragmented Systems to Conversational Intelligence
        </h2>
        <div className="grid md:grid-cols-3 gap-8 mb-10">
          <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200">
            <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <Link2 className="h-8 w-8 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Step 1: Connect</h3>
            <p className="text-gray-600">
              LIA sits above your existing infrastructure. No replacement. No migration. It connects to your systems, documents, emails, and data sources.
            </p>
          </div>
          <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200">
            <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <Brain className="h-8 w-8 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Step 2: Understand</h3>
            <p className="text-gray-600">
              Semantic intelligence maps relationships and context. Finance data connects to operations. Historical decisions link to current status. Fragments become a unified picture.
            </p>
          </div>
          <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200">
            <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <MessageCircle className="h-8 w-8 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Step 3: Ask</h3>
            <p className="text-gray-600">
              Natural language queries. Instant, complete answers. Not just data, but context, relationships, and reasoning.
            </p>
          </div>
        </div>
        <p className="text-center text-gray-600 mb-6">No system replacement. No data migration. LIA sits above your existing infrastructure.</p>
        <p className="text-center text-lg italic text-gray-700">Would you hire someone who needed 2 days to answer every question? That's your current system. LIA is the person who answers in 30 seconds.</p>
      </section>

      {/* Proof Section: Real Outcomes */}
      <section className="bg-white py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-10 sm:mb-12 text-center">
            What Changes When Intelligence Is Accessible
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
              <h3 className="font-bold text-gray-900 mb-2">Finance Director</h3>
              <p className="text-gray-600">Reduced budget variance analysis from 2 days to 4 minutes. Questions that required reconciling three spreadsheets now get answered conversationally.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
              <h3 className="font-bold text-gray-900 mb-2">Supply Chain Manager</h3>
              <p className="text-gray-600">Eliminated 15 hours per week of status update meetings. Questions that needed calling three warehouses now query all systems instantly.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
              <h3 className="font-bold text-gray-900 mb-2">Compliance Officer</h3>
              <p className="text-gray-600">Automated regulatory requirement checking across 47 jurisdictions. Compliance queries that needed manual research now include complete context automatically.</p>
            </div>
          </div>
          <p className="text-center text-gray-600 italic">Institutional knowledge is worthless if nobody can access it. LIA makes it accessible.</p>
        </div>
      </section>

      {/* Data Sovereignty */}
      <section className="bg-gradient-to-br from-primary-50 to-primary-100 py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-10 sm:mb-12 text-center">
            Built for Organisations Where Data Sovereignty Isn't Negotiable
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-10">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-primary-200">
              <Shield className="h-10 w-10 text-primary-600 mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">Sovereign Deployment</h3>
              <p className="text-gray-600">Runs entirely on your infrastructure. Complete control. Your data never leaves your defined boundaries.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-primary-200">
              <Shield className="h-10 w-10 text-primary-600 mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">Secure SaaS</h3>
              <p className="text-gray-600">Tokenised billing. Guaranteed data isolation. Deployed in your jurisdiction with your security requirements.</p>
            </div>
          </div>
          <p className="text-center text-xl font-bold text-primary-700">Your intelligence. Your control. Your jurisdiction.</p>
        </div>
      </section>

      {/* Final Call to Action */}
      <section id="pricing" className="bg-white py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-10 sm:mb-12 text-center">
            What's the Cost of Not Knowing What You Already Know?
          </h2>
          <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-8 mb-10">
            <div className="text-center p-6 border border-gray-200 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">See LIA in Action</h3>
              <p className="text-gray-600 mb-4">Watch a 3-minute demo of LIA answering questions in your industry.</p>
              <Link to="/how-to-use" className="text-primary-600 hover:text-primary-700 font-semibold inline-flex items-center">
                How It Works <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
            <div className="text-center p-6 border border-gray-200 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Calculate Your Cost</h3>
              <p className="text-gray-600 mb-4">How much is information fragmentation costing you?</p>
              <Link to="/contact" className="text-primary-600 hover:text-primary-700 font-semibold inline-flex items-center">
                Get in touch <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
            <div className="text-center p-6 border border-gray-200 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Book a Discovery Call</h3>
              <p className="text-gray-600 mb-4">Show us one question you need answered. We'll show you how LIA finds it.</p>
              <Link to="/contact" className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors font-semibold inline-flex items-center">
                Book a Discovery Call <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
          <p className="text-center text-lg italic text-gray-700">30-day pilot. One use case. Measurable outcomes. See if your organisation can finally answer its own questions.</p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default Home;
